import React, { useEffect } from "react";
import { isBrowser } from "react-device-detect";
import { useTranslation, Trans } from "react-i18next";

import { Button, Image, Link } from "@noom/wax-component-library";

import { FullscreenMessage, TryAgainVideo } from "@/components";
import {
  NoomEmailAddressLink,
  NoomSupportLink,
} from "@/components/noom-branding";
import { ErrorState } from "@/constants";
import { useAppContext } from "@/contexts";
import { useTrackEvent } from "@/hooks";
import { getDisplayName } from "@/models";
import { buildAuthenticatedUrlAppLink } from "@/utils/deeplink";
import { isB2CPartner, isHighmarkPartner } from "@/utils/partners";

type ErrorStateTextConfig = {
  button?: React.ReactElement;
  callToAction: React.ReactNode;
  errorMessage: string;
  visual?: React.ReactElement;
};

type ErrorStateB2CWhitelist = Extract<
  ErrorState,
  ErrorState.DEFAULT | ErrorState.CODE_CLAIMED | ErrorState.ACCOUNT_EXISTS
>;

export const ErrorView: React.FC = () => {
  const {
    eligibilityInfo,
    errorState,
    maskedEmail,
    partnerInfo,
    surveyMetadata,
  } = useAppContext();
  const {
    trackAnonEvent,
    trackIdentifiedEvent,
    trackSurveyAnonEvent,
    trackSurveyIdentifiedEvent,
  } = useTrackEvent();
  const { t } = useTranslation();

  const goLiveDate =
    errorState === ErrorState.BEFORE_GOLIVE_DATE &&
    partnerInfo?.goLiveDate?.toLocaleString([], {
      dateStyle: "full",
      ...((partnerInfo?.goLiveDate?.getHours() > 0 ||
        (partnerInfo?.goLiveDate?.getHours() === 0 &&
          partnerInfo?.goLiveDate?.getMinutes() > 0)) && {
        timeStyle: "short",
      }),
    });

  // Highmark partners always show the time in the go-live date
  const goLiveDateWithTime =
    errorState === ErrorState.BEFORE_GOLIVE_DATE &&
    partnerInfo?.goLiveDate?.toLocaleString([], {
      dateStyle: "full",
      timeStyle: "short",
    });

  const effectiveDate =
    errorState === ErrorState.PRIOR_TO_EFFECTIVE_DATE
      ? eligibilityInfo?.effectiveDate?.toLocaleString([], {
          dateStyle: "full",
          timeZone: "UTC",
        }) || ""
      : "";

  const errorTextConfigs: { [key in ErrorState]: ErrorStateTextConfig } = {
    [ErrorState.CODE_CLAIMED]: {
      callToAction: (
        <Trans i18nKey="error.codeClaimed.callToAction">
          Each signup link contains a code that lets one person sign up for Noom
          - and it looks like this code has already been claimed. Please contact
          your employer for support. If you believe this message is an error,
          contact
          {NoomEmailAddressLink}
          for assistance.
        </Trans>
      ),
      errorMessage: t("error.codeClaimed.errorMessage"),
    },
    [ErrorState.DEFAULT]: {
      callToAction: (
        <Trans i18nKey="error.default.callToAction">
          Please reach out to
          {NoomEmailAddressLink}
          if you have questions or need help.
        </Trans>
      ),
      errorMessage: t("error.default.errorMessage"),
    },
    [ErrorState.ELIGIBILITY_CHECK_EXISTING_ACCOUNT]: {
      callToAction: (
        <Trans i18nKey="error.eligibilityCheckExistingAccount.callToAction">
          Each eligible user can sign up for Noom one time, and a spot has
          already been claimed with the information you provided. Please contact
          your Noom sponsor for support.
          <br />
          <br />
          If you believe this message is an error, contact
          {NoomEmailAddressLink} for assistance.
        </Trans>
      ),
      errorMessage: t("error.eligibilityCheckExistingAccount.errorMessage"),
    },
    [ErrorState.ACCOUNT_EXISTS]: {
      callToAction: (
        <Trans i18nKey="error.existingAccount.callToAction">
          Please login to your existing account via the Noom app. If you
          can&apos;t remember your password, you can
          <a href="https://account.noom.com/password-reset">
            reset your password here
          </a>
          . Still having problems? Contact {NoomEmailAddressLink} for additional
          support.
        </Trans>
      ),
      errorMessage: t("error.existingAccount.errorMessage"),
    },
    [ErrorState.SSO_ACCOUNT_EXISTS]: {
      callToAction: (
        <Trans i18nKey="error.ssoExistingAccount.callToAction">
          Please login to your existing account via the Noom app. If you
          can&apos;t remember your password, you can
          <a href="https://account.noom.com/password-reset">
            reset your password here
          </a>
          . Still having problems? Contact {NoomEmailAddressLink} for additional
          support.
        </Trans>
      ),
      errorMessage: t("error.ssoExistingAccount.errorMessage"),
      visual: <Image alt="" margin="auto" src="/assets/img/thumbs-up.svg" />,
    },
    [ErrorState.EXISTING_VIRGIN_PULSE_USER]: {
      callToAction: (
        <Trans i18nKey="error.existingVirginPulseUser.callToAction">
          Having trouble accessing your account or have a question? Please reach
          out to
          {NoomEmailAddressLink}
          for assistance.
        </Trans>
      ),
      errorMessage: t("error.existingVirginPulseUser.errorMessage", {
        context: maskedEmail ? "hasEmail" : "",
        email: maskedEmail,
      }),
    },
    [ErrorState.BATCH_FULL]: {
      callToAction: (
        <Trans i18nKey="error.fullBatch.callToAction">
          We&apos;re sorry about that. Please reach out to your employer if you
          have any questions about program capacity. Other issues? Drop us a
          line at
          {NoomEmailAddressLink}.
        </Trans>
      ),
      errorMessage: t("error.fullBatch.errorMessage"),
    },
    [ErrorState.SUPPORT_REQUEST_CREATED]: {
      callToAction: (
        <Trans i18nKey="error.supportRequestCreated.callToAction">
          Unfortunately we encountered an issue while setting up your account.
          Hang tight - we&apos;ve alerted our support team and they will reach
          out to you soon to set up your Noom program.
          <br />
          <br />
          If you have any questions before then, you can reach out to us at
          partnersupport@noom.com.
        </Trans>
      ),
      errorMessage: t("error.supportRequestCreated.errorMessage"),
    },
    [ErrorState.PENDING_DELETION_REQUEST]: {
      callToAction: (
        <Trans i18nKey="error.pendingDeletion.callToAction">
          Our records show that an account using this email address was recently
          deleted. For privacy reasons we cannot create a new account using that
          email right now.
          <br />
          <br />
          Please sign up again using a different email address. If you are still
          having issues you can reach out to our customer support team at
          partnersupport@noom.com.
        </Trans>
      ),
      errorMessage: t("error.pendingDeletion.errorMessage"),
    },
    [ErrorState.BEFORE_GOLIVE_DATE]: {
      callToAction: (() => {
        const hasFutureGoLiveDate = Boolean(
          partnerInfo?.goLiveDate && partnerInfo.goLiveDate > new Date(),
        );
        if (isHighmarkPartner(partnerInfo)) {
          if (hasFutureGoLiveDate) {
            return (
              <Trans i18nKey="error.beforeGoliveDate.callToAction_highmark">
                We haven&apos;t launched with your health plan yet. Please come
                back on {goLiveDateWithTime} local time to sign up for Noom.
              </Trans>
            );
          }
          return (
            <Trans i18nKey="error.beforeGoliveDate.callToAction_highmark_noDate">
              We haven&apos;t launched with your health plan yet. Please check
              back later to sign up for Noom.
            </Trans>
          );
        }
        if (hasFutureGoLiveDate) {
          return (
            <Trans i18nKey="error.beforeGoliveDate.callToAction">
              We haven&apos;t launched with {getDisplayName(partnerInfo)} yet.
              Please come back on {goLiveDate} to sign up for Noom.
            </Trans>
          );
        }
        return (
          <Trans i18nKey="error.beforeGoliveDate.callToAction_noDate">
            We haven&apos;t launched with {getDisplayName(partnerInfo)} yet.
            Please check back later to sign up for Noom.
          </Trans>
        );
      })(),
      errorMessage: t("error.beforeGoliveDate.errorMessage"),
    },
    [ErrorState.FAILED_ELIGIBILITY_CHECK]: {
      callToAction: isHighmarkPartner(partnerInfo) ? (
        <Trans i18nKey="error.failedEligibilityCheck.callToAction_highmark">
          We appreciate your interest in Noom, but it looks like the information
          you entered doesn&apos;t match our eligibility records. Please
          double-check that your details match your member ID card. Alternatively,
          you can enroll through your health plan&apos;s member portal.
          <br />
          <br />
          If you still don&apos;t have access and are eager to use Noom, you can
          sign up and pay for a membership anytime at{" "}
          <a href="https://www.noom.com">Noom.com</a>.
          <br />
          <br />
          For any questions or support, email us at {NoomEmailAddressLink} with
          your name, email, and membership sponsor. Our team is here to help.
        </Trans>
      ) : (
        <Trans i18nKey="error.failedEligibilityCheck.callToAction">
          We appreciate your interest in Noom, but it looks like the information
          you entered doesn&apos;t match our eligibility records. Please
          double-check your details, like using your full legal name.
          <br />
          <br />
          If you still don&apos;t have access and are eager to use Noom, you can
          sign up and pay for a membership anytime at{" "}
          <a href="https://www.noom.com">noom.com</a>.
          <br />
          <br />
          For any questions or support, email us at {NoomEmailAddressLink} with
          your name, email, and membership sponsor. Our team is here to help.
        </Trans>
      ),
      errorMessage: t("error.failedEligibilityCheck.errorMessage"),
    },
    [ErrorState.SSO_INELIGIBLE]: {
      callToAction: (
        <Trans i18nKey="error.ssoIneligible.callToAction">
          Something went wrong while trying to confirm your eligibility.
          <br />
          <br />
          Please email {NoomEmailAddressLink} with your name, email, and
          membership sponsor and our team will do our best to help you out.
          <br />
          <br />
          If you aren&apos;t eligible and still want to use Noom, you can sign
          up and pay for a membership at
          <a href="https://www.noom.com">noom.com</a>.
        </Trans>
      ),
      errorMessage: t("error.ssoIneligible.errorMessage"),
    },
    [ErrorState.PAST_CONTRACT_END_DATE]: {
      callToAction: (
        <Trans i18nKey="error.pastContractEndDate.callToAction">
          Thanks for your interest in Noom! Right now,{" "}
          {getDisplayName(partnerInfo)} isn&apos;t sponsoring more Noom
          memberships. However, you can still sign up for the Noom program on
          your own by going to <a href="https://www.noom.com">noom.com</a>.
          <br />
          <br />
          If you have questions or are concerned you&apos;re seeing this message
          in error, please email {NoomEmailAddressLink} or contact your Noom
          sponsor.
        </Trans>
      ),
      errorMessage: t("error.pastContractEndDate.errorMessage", {
        partnerName: getDisplayName(partnerInfo),
      }),
    },
    [ErrorState.PRIOR_TO_EFFECTIVE_DATE]: {
      callToAction: t("error.priorToEffectiveDate.callToAction", {
        effectiveDate,
      }),
      errorMessage: t("error.priorToEffectiveDate.errorMessage"),
    },
    [ErrorState.ELIGIBILITY_CHECK_API_ERROR]: {
      callToAction: t("error.eligibilityCheckApiError.callToAction"),
      errorMessage: t("error.eligibilityCheckApiError.errorMessage"),
    },
    [ErrorState.ELIGIBILITY_CHECK_MULTIPLE_MATCHES]: {
      callToAction: (
        <Trans i18nKey="error.eligibilityCheckMultipleMatches.callToAction">
          Please email {NoomEmailAddressLink} with your name, email, and
          membership sponsor and our team will do our best to help you out.
        </Trans>
      ),
      errorMessage: t("error.eligibilityCheckMultipleMatches.errorMessage"),
    },
    [ErrorState.MISSING_AUTOLOGIN]: {
      button: isBrowser ? undefined : (
        <Link
          href={buildAuthenticatedUrlAppLink(
            `${window.location.origin}/scale-opt-in`,
          )}
        >
          <Button as="a" colorScheme="primary" size="xl">
            {t("error.missingAutologin.openInApp")}
          </Button>
        </Link>
      ),
      callToAction: t("error.missingAutologin.callToAction", {
        context: isBrowser ? "" : "mobile",
      }),
      errorMessage: t("error.missingAutologin.errorMessage"),
    },
  };

  const errorTextB2CConfigs: {
    [key in ErrorStateB2CWhitelist]: ErrorStateTextConfig;
  } = {
    [ErrorState.CODE_CLAIMED]: {
      callToAction: (
        <Trans
          i18nKey="error.codeClaimed.callToAction_b2c"
          components={{ supportLink: NoomSupportLink }}
        />
      ),
      errorMessage: t("error.codeClaimed.errorMessage"),
    },
    [ErrorState.DEFAULT]: {
      callToAction: (
        <Trans
          i18nKey="error.default.callToAction_b2c"
          components={{ supportLink: NoomSupportLink }}
        />
      ),
      errorMessage: t("error.default.errorMessage"),
    },
    [ErrorState.ACCOUNT_EXISTS]: {
      callToAction: (
        <Trans
          i18nKey="error.existingAccount.callToAction_b2c"
          components={{ supportLink: NoomSupportLink }}
        />
      ),
      errorMessage: t("error.existingAccount.errorMessage"),
    },
  };

  const errorText =
    (isB2CPartner(partnerInfo) &&
      errorTextB2CConfigs[errorState as unknown as ErrorStateB2CWhitelist]) ||
    errorTextConfigs[errorState];

  const { button, callToAction, errorMessage, visual } = errorText;

  useEffect(() => {
    if (surveyMetadata) {
      trackSurveyAnonEvent("b2bSurveyAnonErrorPageViewed", {
        errorType: errorState,
      });
      trackSurveyIdentifiedEvent("b2bSurveyIdentifiedErrorPageViewed", {
        errorType: errorState,
      });
    } else {
      trackAnonEvent("b2bEnrollmentAnonErrorPageViewed", {
        b2bErrorType: errorState,
      });
      trackIdentifiedEvent("b2bEnrollmentIdentifiedErrorPageViewed", {
        b2bErrorType: errorState,
      });
    }
  }, [errorState]);

  return (
    <FullscreenMessage
      button={button}
      heading={errorMessage}
      message={callToAction}
      visual={visual || <TryAgainVideo />}
    />
  );
};
